import * as React from 'react';
import {usePerformanceReport} from '@shopify/react-performance';
import {AppProvider as OSUIProvider} from '@shopify/online-store-ui';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import Bugsnag from '@bugsnag/js';
import {ServerData} from 'utilities/server-data';

import {GraphQL} from './GraphQL';
import {I18n} from './I18n';
import {Metadata} from './Metadata';
import {Polaris} from './Polaris';
import {Toast} from './Toast';
import Routes from './Routes';
import styles from './App.scss';

import '@shopify/polaris/build/esm/styles.css';
import '@shopify/online-store-ui/build/esm/styles.css';
import '@shopify/legacy-rich-text-editor/build/esm/styles.css';

let host =
  new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.search,
  ).get('host') || '';

if (host === '') {
  const rootElement =
    typeof document === 'undefined' ? null : document.getElementById('app');
  if (rootElement != null) {
    host = rootElement.dataset.host!;
  }
}

export interface AppProperties {
  url: URL;
  data: ServerData;
}

export function App({url, data}: AppProperties) {
  usePerformanceReport('/performance_report');

  // eslint-disable-next-line no-process-env
  const releaseStage = process.env.NODE_ENV;

  Bugsnag.start({
    apiKey: data.bugsnag.apiKey,
    appVersion: data.bugsnag.appVersion,
    enabledReleaseStages: ['production'],
    enabledBreadcrumbTypes: ['error', 'manual', 'navigation', 'user'],
    maxBreadcrumbs: 40,
    plugins: [new BugsnagPluginReact()],
    releaseStage,
    user: {
      id: data.shop.shopId,
      name: data.shop.shopDomain,
    },
    onError: (event) => {
      /**
       * Ignore permission errors, they cause no user impact and are caused by iFrame timing/capability support
       * They can occur in a number of places where AppBridge is used - so handling them globally rather than case by case
       **/
      if (event.originalError.type === 'APP::ERROR::PERMISSION') {
        return false;
      }
    },
  });

  const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

  return (
    <React.StrictMode>
      <div className={styles.AppWrapper}>
        <ErrorBoundary>
          <Metadata />
          <GraphQL url={url} locale={data.locale}>
            <I18n locale={data.locale}>
              <Polaris>
                <OSUIProvider>
                  <Toast>
                    <Routes />
                  </Toast>
                </OSUIProvider>
              </Polaris>
            </I18n>
          </GraphQL>
        </ErrorBoundary>
      </div>
    </React.StrictMode>
  );
}
